<template>
  <div class="ilib-page">
    <APageHead title="강사 자료실 도서 관리" sub="강사 자료실에 제공되는 도서와 부가 자료(음원·PDF)를 관리합니다.">
      <template #actions>
        <ABtn icon="refresh" @click="getBooks">새로고침</ABtn>
        <ABtn kind="primary" icon="add" @click="openCreateModal">도서 등록</ABtn>
      </template>
    </APageHead>

    <!-- 검색 -->
    <ACard overflow="visible">
      <div class="ilib-page__filters">
        <div class="p-field">
          <label class="p-field__label"><i class="material-icons-round p-field__label-icon">search</i>검색</label>
          <input
            v-model="searchQuery"
            type="text"
            class="p-input"
            placeholder="도서명·바코드·출판사 검색..."
            @keyup.enter="handleSearch"
          />
        </div>
        <div class="ilib-page__filters-action">
          <ABtn kind="primary" icon="search" @click="handleSearch">조회</ABtn>
        </div>
      </div>
    </ACard>

    <!-- 도서 그리드 -->
    <ACard>
      <template #title>
        <span class="ilib-page__sec-title"><i class="material-icons-round ilib-page__sec-icon">auto_stories</i>도서 목록</span>
      </template>
      <template #action>
        <APill kind="info">총 {{ formatNum(totalCount) }}권</APill>
      </template>

      <div v-if="loading" class="ilib-page__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>도서 목록을 불러오는 중...</p>
      </div>

      <AEmpty
        v-else-if="books.length === 0"
        title="등록된 도서가 없어요"
        sub="우측 상단 '도서 등록' 버튼으로 첫 도서를 등록해보세요."
      />

      <div v-else class="ilib-page__grid">
        <article v-for="book in books" :key="book.idx" class="ilib-page__book">
          <div class="ilib-page__cover" @click="openAssetsModal(book)">
            <img v-if="book.cover_image_url" :src="book.cover_image_url" :alt="book.title" />
            <div v-else class="ilib-page__cover-fallback">
              <i class="material-icons-round">menu_book</i>
            </div>
            <div class="ilib-page__cover-hover"><i class="material-icons-round">folder_open</i>자료 관리</div>
          </div>
          <div class="ilib-page__book-info">
            <h6 class="ilib-page__book-title" :title="book.title">{{ book.title }}</h6>
            <div class="ilib-page__book-meta">
              <span class="ilib-page__mono">{{ book.barcode }}</span>
              <span v-if="book.publisher">{{ book.publisher }}</span>
            </div>
            <div class="ilib-page__book-pills">
              <APill v-if="book.level" kind="yellow">{{ book.level }}</APill>
              <span class="ilib-page__book-date">{{ formatDate(book.created_at) }}</span>
            </div>
          </div>
          <div class="ilib-page__book-actions">
            <ABtn size="sm" icon="folder_open" @click="openAssetsModal(book)">자료</ABtn>
            <ABtn size="sm" icon="edit" @click="openEditModal(book)">수정</ABtn>
            <ABtn size="sm" kind="danger" icon="delete" icon-only @click="deleteBook(book)" />
          </div>
        </article>
      </div>

      <APagination
        v-if="totalCount > itemsPerPage"
        :current-page="currentPage"
        :total="totalCount"
        :page-size="itemsPerPage"
        @change="(p) => { currentPage = p; getBooks(); }"
      />
    </ACard>

    <!-- 도서 등록/수정 모달 -->
    <AModal v-model="showBookModal" size="md">
      <template #title>
        <span class="ilib-page__modal-title">
          <i class="material-icons-round ilib-page__modal-icon">{{ isEditMode ? 'edit' : 'library_add' }}</i>
          {{ isEditMode ? '도서 정보 수정' : '도서 등록' }}
        </span>
      </template>
      <template #sub>
        {{ isEditMode ? '보낸 필드만 수정됩니다. 표지는 새 파일 선택 시에만 교체됩니다.' : '표지 이미지·도서명·바코드는 필수입니다.' }}
      </template>

      <form class="ilib-page__form" @submit.prevent="saveBook">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">qr_code</i>
            바코드 <span v-if="!isEditMode" class="p-field__req">*</span>
          </label>
          <input
            v-model.trim="bookForm.barcode"
            type="text"
            class="p-input"
            placeholder="ISBN 등 도서 바코드"
            :readonly="isEditMode"
            :class="{ 'ilib-page__input-readonly': isEditMode }"
          />
          <div v-if="isEditMode" class="p-field__hint"><i>info</i>바코드는 수정할 수 없습니다.</div>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">title</i>
            도서명 <span class="p-field__req">*</span>
          </label>
          <input v-model.trim="bookForm.title" type="text" class="p-input" placeholder="도서명" />
        </div>

        <div class="p-form-grid p-form-grid--2">
          <div class="p-field">
            <label class="p-field__label"><i class="material-icons-round p-field__label-icon">business</i>출판사</label>
            <input v-model.trim="bookForm.publisher" type="text" class="p-input" placeholder="출판사 (선택)" />
          </div>
          <div class="p-field">
            <label class="p-field__label"><i class="material-icons-round p-field__label-icon">signal_cellular_alt</i>레벨</label>
            <input v-model.trim="bookForm.level" type="text" class="p-input" placeholder="레벨 (선택, 자유 입력)" />
          </div>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">image</i>
            표지 이미지 <span v-if="!isEditMode" class="p-field__req">*</span>
          </label>
          <input
            ref="coverInput"
            type="file"
            class="p-input p-input--file"
            accept=".jpg,.jpeg,.png,.gif,.webp"
            @change="onCoverChange"
          />
          <div class="p-field__hint"><i>info</i>.jpg .jpeg .png .gif .webp 형식만 지원합니다.</div>
        </div>

        <div v-if="coverPreview || (isEditMode && editingBook?.cover_image_url)" class="ilib-page__cover-preview">
          <img :src="coverPreview || editingBook.cover_image_url" alt="표지 미리보기" />
          <span>{{ coverPreview ? '새 표지 미리보기' : '현재 표지' }}</span>
        </div>
      </form>

      <template #foot>
        <ABtn @click="showBookModal = false">취소</ABtn>
        <ABtn
          kind="primary"
          :icon="isEditMode ? 'save' : 'add'"
          :disabled="isSaving"
          :loading="isSaving"
          @click="saveBook"
        >{{ isEditMode ? '저장' : '등록' }}</ABtn>
      </template>
    </AModal>

    <!-- 자료 관리 모달 -->
    <AModal v-model="showAssetsModal" size="lg">
      <template #title>
        <span class="ilib-page__modal-title">
          <i class="material-icons-round ilib-page__modal-icon">folder_open</i>
          자료 관리 — {{ assetsBook?.title }}
        </span>
      </template>
      <template #sub>
        <span class="ilib-page__mono">{{ assetsBook?.barcode }}</span> · 음원과 PDF 자료를 업로드하고 관리합니다. 같은 파일명으로 업로드하면 덮어쓰기됩니다.
      </template>

      <div v-if="assetsLoading" class="ilib-page__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>자료 목록을 불러오는 중...</p>
      </div>

      <div v-else class="ilib-page__assets">
        <section v-for="type in assetTypes" :key="type.value" class="ilib-page__asset-sec">
          <div class="ilib-page__asset-head">
            <span class="ilib-page__asset-title">
              <i class="material-icons-round">{{ type.icon }}</i>
              {{ type.label }}
              <APill kind="default">{{ assetList(type.value).length }}개</APill>
            </span>
            <label class="ilib-page__upload-btn">
              <input
                type="file"
                multiple
                :accept="type.accept"
                class="ilib-page__upload-input"
                @change="(e) => uploadAssets(type.value, e)"
              />
              <i class="material-icons-round">upload_file</i>파일 업로드
            </label>
          </div>

          <AEmpty
            v-if="assetList(type.value).length === 0"
            size="sm"
            :title="`등록된 ${type.label} 자료가 없어요`"
          />

          <ul v-else class="ilib-page__asset-list">
            <li v-for="file in assetList(type.value)" :key="file.filename" class="ilib-page__asset-item">
              <i class="material-icons-round ilib-page__asset-icon">{{ type.fileIcon }}</i>
              <div class="ilib-page__asset-info">
                <span class="ilib-page__asset-name" :title="file.filename">{{ file.filename }}</span>
                <small>{{ formatFileSize(file.file_size) }} · {{ formatDate(file.created_at) }}</small>
              </div>
              <ABtn size="sm" kind="danger" icon="delete" icon-only @click="deleteAsset(type.value, file)" />
            </li>
          </ul>
        </section>
      </div>

      <template #foot>
        <ABtn @click="showAssetsModal = false">닫기</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import APageHead from "@/components/Pineapple/APageHead.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import APill from "@/components/Pineapple/APill.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import AModal from "@/components/Pineapple/AModal.vue";

export default {
  name: "HelpDeskInstructorLibrary",
  components: { APageHead, ABtn, ACard, APill, AEmpty, APagination, AModal },
  data() {
    return {
      books: [],
      totalCount: 0,
      currentPage: 1,
      itemsPerPage: 24,
      searchQuery: "",
      loading: false,
      // 등록/수정 모달
      showBookModal: false,
      isEditMode: false,
      isSaving: false,
      editingBook: null,
      bookForm: { barcode: "", title: "", publisher: "", level: "" },
      coverFile: null,
      coverPreview: null,
      // 자료 관리 모달
      showAssetsModal: false,
      assetsBook: null,
      assetsLoading: false,
      materials: { audio: [], pdf: [] },
      assetTypes: [
        {
          value: "audio", label: "음원", icon: "graphic_eq", fileIcon: "audio_file",
          accept: ".mp3,.wav,.ogg,.m4a,.aac,.flac",
        },
        {
          value: "pdf", label: "PDF", icon: "picture_as_pdf", fileIcon: "description",
          accept: ".pdf",
        },
      ],
    };
  },
  mounted() {
    this.getBooks();
  },
  beforeUnmount() {
    this.revokeCoverPreview();
  },
  methods: {
    formatNum(n) { return Number(n || 0).toLocaleString(); },
    formatDate(s) {
      if (!s) return "-";
      return new Date(s).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
    },
    formatFileSize(bytes) {
      const n = Number(bytes || 0);
      if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
      if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
      return `${n} B`;
    },
    assetList(type) {
      return this.materials[type] || [];
    },
    handleSearch() {
      this.currentPage = 1;
      this.getBooks();
    },
    async getBooks() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: "/emp/instructor/library/books",
          params: {
            search: this.searchQuery || undefined,
            limit: this.itemsPerPage,
            offset: (this.currentPage - 1) * this.itemsPerPage,
          },
          loadingBar: true,
        });
        this.books = res.data.items || [];
        this.totalCount = res.data.total_count || 0;
      } catch (error) {
        console.error("도서 목록 조회 실패:", error);
        this.$swal({ icon: "error", title: "조회 실패", text: "도서 목록을 불러오는데 실패했습니다." });
      } finally {
        this.loading = false;
      }
    },
    // ── 등록/수정 ──
    revokeCoverPreview() {
      if (this.coverPreview) {
        URL.revokeObjectURL(this.coverPreview);
        this.coverPreview = null;
      }
    },
    resetBookForm() {
      this.bookForm = { barcode: "", title: "", publisher: "", level: "" };
      this.coverFile = null;
      this.revokeCoverPreview();
      if (this.$refs.coverInput) this.$refs.coverInput.value = "";
    },
    openCreateModal() {
      this.isEditMode = false;
      this.editingBook = null;
      this.resetBookForm();
      this.showBookModal = true;
    },
    openEditModal(book) {
      this.isEditMode = true;
      this.editingBook = book;
      this.resetBookForm();
      this.bookForm = {
        barcode: book.barcode,
        title: book.title,
        publisher: book.publisher || "",
        level: book.level || "",
      };
      this.showBookModal = true;
    },
    onCoverChange(e) {
      const file = e.target.files[0];
      this.revokeCoverPreview();
      this.coverFile = file || null;
      if (file) this.coverPreview = URL.createObjectURL(file);
    },
    async saveBook() {
      if (!this.bookForm.title) {
        this.$swal({ icon: "warning", title: "도서명을 입력해주세요.", showConfirmButton: true });
        return;
      }
      if (!this.isEditMode) {
        if (!this.bookForm.barcode) {
          this.$swal({ icon: "warning", title: "바코드를 입력해주세요.", showConfirmButton: true });
          return;
        }
        if (!this.coverFile) {
          this.$swal({ icon: "warning", title: "표지 이미지를 선택해주세요.", showConfirmButton: true });
          return;
        }
      }

      const formData = new FormData();
      formData.append("title", this.bookForm.title);
      formData.append("publisher", this.bookForm.publisher);
      formData.append("level", this.bookForm.level);
      if (this.coverFile) formData.append("cover", this.coverFile);

      this.isSaving = true;
      try {
        let res;
        if (this.isEditMode) {
          res = await this.$axios({
            method: "put",
            url: `/emp/instructor/library/books/${this.editingBook.idx}`,
            data: formData,
            loadingBar: true,
          });
        } else {
          formData.append("barcode", this.bookForm.barcode);
          res = await this.$axios({
            method: "post",
            url: "/emp/instructor/library/books",
            data: formData,
            loadingBar: true,
          });
        }
        this.showBookModal = false;
        this.$swal({
          icon: "success",
          title: this.isEditMode ? "수정 완료" : "등록 완료",
          text: res.data.message || "",
          timer: 2000,
        });
        this.getBooks();
      } catch (error) {
        console.error("도서 저장 실패:", error);
        const text = error.response?.status === 409
          ? "이미 등록된 바코드입니다."
          : error.response?.data?.detail || error.response?.data?.message || "도서 저장에 실패했습니다.";
        this.$swal({ icon: "error", title: this.isEditMode ? "수정 실패" : "등록 실패", text, showConfirmButton: true });
      } finally {
        this.isSaving = false;
      }
    },
    async deleteBook(book) {
      const confirm = await this.$swal({
        icon: "warning",
        title: "도서 삭제",
        html: `<b>${book.title}</b><br/>표지와 등록된 자료 파일이 모두 삭제됩니다.<br/>(다운로드 이력은 보존됩니다)`,
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: "삭제",
        cancelButtonText: "취소",
      });
      if (!confirm.isConfirmed) return;

      try {
        await this.$axios({
          method: "delete",
          url: `/emp/instructor/library/books/${book.idx}`,
          loadingBar: true,
        });
        this.$swal({ icon: "success", title: "삭제 완료", timer: 2000 });
        if (this.books.length === 1 && this.currentPage > 1) this.currentPage -= 1;
        this.getBooks();
      } catch (error) {
        console.error("도서 삭제 실패:", error);
        this.$swal({
          icon: "error",
          title: "삭제 실패",
          text: error.response?.data?.detail || error.response?.data?.message || "도서 삭제에 실패했습니다.",
          showConfirmButton: true,
        });
      }
    },
    // ── 자료 관리 ──
    openAssetsModal(book) {
      this.assetsBook = book;
      this.materials = { audio: [], pdf: [] };
      this.showAssetsModal = true;
      this.getAssets();
    },
    async getAssets() {
      this.assetsLoading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/emp/instructor/library/books/${encodeURIComponent(this.assetsBook.barcode)}`,
        });
        const materials = res.data.materials || {};
        this.materials = { audio: materials.audio || [], pdf: materials.pdf || [] };
      } catch (error) {
        console.error("자료 목록 조회 실패:", error);
        this.$swal({ icon: "error", title: "조회 실패", text: "자료 목록을 불러오는데 실패했습니다." });
      } finally {
        this.assetsLoading = false;
      }
    },
    async uploadAssets(assetType, e) {
      const files = Array.from(e.target.files || []);
      e.target.value = "";
      if (files.length === 0) return;

      const formData = new FormData();
      formData.append("asset_type", assetType);
      files.forEach((file) => formData.append("files", file));

      try {
        const res = await this.$axios({
          method: "post",
          url: `/emp/instructor/library/books/${this.assetsBook.idx}/assets`,
          data: formData,
          loadingBar: true,
        });
        const uploaded = res.data.files || [];
        this.$swal({ icon: "success", title: "업로드 완료", text: `${uploaded.length}개 파일이 업로드되었습니다.`, timer: 2000 });
        this.getAssets();
      } catch (error) {
        console.error("자료 업로드 실패:", error);
        this.$swal({
          icon: "error",
          title: "업로드 실패",
          text: error.response?.data?.detail || error.response?.data?.message || "자료 업로드에 실패했습니다.",
          showConfirmButton: true,
        });
      }
    },
    async deleteAsset(assetType, file) {
      const confirm = await this.$swal({
        icon: "warning",
        title: "자료 삭제",
        text: `${file.filename} 파일을 삭제하시겠습니까?`,
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: "삭제",
        cancelButtonText: "취소",
      });
      if (!confirm.isConfirmed) return;

      try {
        await this.$axios({
          method: "delete",
          url: `/emp/instructor/library/books/${this.assetsBook.idx}/assets/${assetType}/${encodeURIComponent(file.filename)}`,
          loadingBar: true,
        });
        this.$swal({ icon: "success", title: "삭제 완료", timer: 2000 });
        this.getAssets();
      } catch (error) {
        console.error("자료 삭제 실패:", error);
        this.$swal({
          icon: "error",
          title: "삭제 실패",
          text: error.response?.data?.detail || error.response?.data?.message || "자료 삭제에 실패했습니다.",
          showConfirmButton: true,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.ilib-page {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__filters {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 12px;
    align-items: end;
  }

  &__filters-action {
    padding-bottom: 1px;
  }

  &__sec-title {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 800; color: $p-ink;
  }

  &__sec-icon { color: $p-point-deep; font-size: 16px !important; }

  &__loading {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    padding: 48px 24px; color: $p-ink-mid;
    p { margin: 0; font-size: 12px; }
  }

  &__mono { font-family: $p-font-mono; font-size: 11px; color: $p-ink-mid; }

  // ── 도서 그리드 ──
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
  }

  &__book {
    display: flex;
    flex-direction: column;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    overflow: hidden;
    background: $p-panel;
    transition: border-color $p-dur $p-ease, box-shadow $p-dur $p-ease;

    &:hover {
      border-color: $p-point-deep;
      box-shadow: $p-shadow;

      .ilib-page__cover-hover { opacity: 1; }
    }
  }

  &__cover {
    position: relative;
    aspect-ratio: 3 / 4;
    background: $p-panel-alt;
    cursor: pointer;

    > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  &__cover-fallback {
    width: 100%;
    height: 100%;
    display: grid;
    place-items: center;
    color: $p-ink-softer;

    > i { font-size: 48px !important; }
  }

  &__cover-hover {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: rgba(31, 27, 20, 0.55);
    color: #fff;
    font-size: 12px;
    font-weight: 800;
    opacity: 0;
    transition: opacity $p-dur $p-ease;

    > i { font-size: 18px !important; }
  }

  &__book-info {
    padding: 10px 12px 6px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__book-title {
    margin: 0;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__book-meta {
    display: flex;
    gap: 8px;
    font-size: 11px;
    color: $p-ink-mid;
    flex-wrap: wrap;
  }

  &__book-pills {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }

  &__book-date {
    font-size: 10px;
    color: $p-ink-soft;
  }

  &__book-actions {
    display: flex;
    gap: 6px;
    padding: 8px 12px 12px;
    margin-top: auto;
  }

  // ── 등록/수정 모달 ──
  &__modal-title {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 15px; font-weight: 800; color: $p-ink;
  }

  &__modal-icon { color: $p-point-deep; font-size: 20px !important; }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__input-readonly {
    background: $p-panel-alt;
    color: $p-ink-mid;
    cursor: not-allowed;
  }

  &__cover-preview {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-md;
    background: $p-panel-alt;

    > img {
      width: 72px;
      height: 96px;
      object-fit: cover;
      border-radius: $p-radius-sm;
      border: 1px solid $p-line;
      flex-shrink: 0;
    }

    > span {
      font-size: 11px;
      font-weight: 700;
      color: $p-ink-mid;
    }
  }

  // ── 자료 관리 모달 ──
  &__assets {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__asset-sec {
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
    overflow: hidden;
  }

  &__asset-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
    background: $p-panel-alt;
    border-bottom: 1px solid $p-line-soft;
  }

  &__asset-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;

    > i { font-size: 17px !important; color: $p-point-deep; }
  }

  &__upload-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 12px;
    border-radius: $p-radius;
    background: $p-point;
    color: $p-ink;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    transition: filter $p-dur $p-ease;

    &:hover { filter: brightness(0.96); }

    > i { font-size: 15px !important; }
  }

  &__upload-input {
    display: none;
  }

  &__asset-list {
    list-style: none;
    margin: 0;
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__asset-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: $p-radius-sm;
    transition: background $p-dur $p-ease;

    &:hover { background: $p-panel-alt; }
  }

  &__asset-icon {
    font-size: 18px !important;
    color: $p-ink-soft;
    flex-shrink: 0;
  }

  &__asset-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;

    > small {
      font-size: 10px;
      color: $p-ink-soft;
    }
  }

  &__asset-name {
    font-size: 12px;
    font-weight: 700;
    color: $p-ink;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
